import React, {
  createContext, useCallback, useContext, useState, useMemo,
} from 'react';
import {
  Box,
  Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { isEmpty, trim } from 'lodash';
import { StringSchema } from 'yup';
import { SuccessSnackbar } from 'components/notifications/SuccessSnackbar';

const useStyles = makeStyles(() => ({
  dialogTitle: {
    paddingBottom: 0,
  },
  dialogActionsSpaceBetween: {
    justifyContent: 'space-between',
  },
}));

export interface DialogOptions {
  type: 'confirm' | 'info' | 'prompt';
  title?: JSX.Element | string;
  content?: JSX.Element | string;
  actionsSpaceBetween?: boolean;
  okText?: string;
  okColor?: 'inherit' | 'primary' | 'secondary';
  cancelText?: string;
  cancelColor?: 'inherit' | 'primary' | 'secondary';
  placeholder?: string;
  promptValueIsOptional?: boolean;
  promptDefaultValue?: string;
  promptSchema?: StringSchema;
}

export type Options = Omit<DialogOptions, 'type'>;

export interface SuccessOptions {
  message: string;
  autoHideDuration?: number;
}

interface DialogContextData {
  confirm: (options: Options) => Promise<boolean>;
  info: (options: Options) => Promise<boolean>;
  prompt: (options: Options) => Promise<false | string>;
  success: (options: SuccessOptions) => void;
}

export const DialogContext = createContext<DialogContextData | undefined>(undefined);

export const useDialog = () => {
  const ctx = useContext(DialogContext);
  if (ctx === undefined) {
    throw new Error('Dialog is undefined in a context');
  }
  return ctx;
};

export const DialogProvider: React.FC<{ children?: React.ReactNode; }> = ({ children }) => {
  const classes = useStyles();
  const [options, setOptions] = useState<DialogOptions | undefined>();
  const [resolver, setResolver] = useState<Function | undefined>();

  const [successOpen, setSuccessOpen] = useState<SuccessOptions | null>(null);
  const success = useCallback((opts: SuccessOptions) => {
    setSuccessOpen(opts);
  }, []);

  const [validationError, setValidationError] = useState('');
  const validateValue = useCallback(async (val) => {
    if (options?.promptSchema) {
      try {
        await options.promptSchema.validate(val);
        setValidationError('');
      } catch (e: any) {
        setValidationError(e.message);
        return false;
      }
    }
    return true;
  }, [options?.promptSchema]);

  const confirm = useCallback((params: Options): Promise<boolean> => new Promise((resolve) => {
    setOptions({ ...params, type: 'confirm' });
    setResolver(() => resolve);
  }), []);

  const info = useCallback((params: Options): Promise<boolean> => new Promise((resolve) => {
    setOptions({ ...params, type: 'info' });
    setResolver(() => resolve);
  }), []);

  const [value, setValue] = useState('');
  const prompt = useCallback(
    (params: Options): Promise<false | string> => new Promise((resolve) => {
      setOptions({ ...params, type: 'prompt' });
      setValue(params.promptDefaultValue ?? '');
      setValidationError('');
      setResolver(() => resolve);
    }),
    [],
  );

  const handleClose = useCallback((reason: boolean | null = false) => {
    if (resolver) resolver(reason);
    setOptions(undefined);
  }, [resolver, setOptions]);

  const handleOk = useCallback(async (promptValue?: string) => {
    const valid = await validateValue(promptValue ?? '');
    if (valid) {
      if (resolver) resolver(promptValue ?? true);
      setOptions(undefined);
    }
  }, [resolver, validateValue, setOptions]);

  const labelId = 'dialog-hook-modal';
  const contextParams = useMemo(() => ({
    confirm,
    info,
    prompt,
    success,
  }), [confirm, info, prompt, success]);

  return (
    <>
      <DialogContext.Provider value={contextParams}>
        {children}
      </DialogContext.Provider>
      <SuccessSnackbar
        open={!!successOpen}
        autoHideDuration={successOpen?.autoHideDuration}
        onClose={() => setSuccessOpen(null)}
      >
        {successOpen?.message}
      </SuccessSnackbar>
      {options && (
        <Dialog
          open={!!options}
          onClose={() => handleClose(null)}
          aria-labelledby={labelId}
        >
          {options.title && (
            <DialogTitle id={labelId} className={classes.dialogTitle}>
              {options.title}
            </DialogTitle>
          )}
          {(options.content || options.type === 'prompt') && (
            <DialogContent>
              {options.content}
              {options.type === 'prompt' && (
                <Box marginTop={2}>
                  <TextField
                    fullWidth
                    autoFocus
                    value={value}
                    placeholder={options.placeholder}
                    onChange={(e) => {
                      setValue(e.target.value);
                      validateValue(e.target.value);
                    }}
                    onBlur={(e) => validateValue(e.target.value)}
                    error={!!validationError}
                    helperText={validationError}
                  />
                </Box>
              )}
            </DialogContent>
          )}
          <DialogActions className={options.actionsSpaceBetween ? classes.dialogActionsSpaceBetween : ''}>
            <Button
              color={options.okColor || 'primary'}
              onClick={() => handleOk(options.type === 'prompt' ? value : undefined)}
              disabled={options.type === 'prompt' && !options.promptValueIsOptional && isEmpty(trim(value))}
            >
              {options.okText ?? 'Ok'}
            </Button>
            {['confirm', 'prompt'].includes(options?.type) && (
              <Button onClick={() => handleClose()} color={options.cancelColor}>
                {options.cancelText ?? 'Cancel'}
              </Button>
            )}
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};
